import { type OperatorSlug, OTHER_OPERATOR_SLUG } from '@ev/shared'
import { useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Overlay } from '../../../about/Overlay'
import { useStrings } from '../../../i18n'
import { useCardStore } from '../../../state/cardStore'
import type { ChargeCard } from '../../../state/chargeCards'
import { OPERATOR_OPTIONS } from '../facets'

/**
 * One dialog for defining a card and for changing one.
 *
 * The same surface on purpose: "which brands does this card pay at" is the only
 * question either task asks, and a driver who has just corrected eVolt's list
 * should not meet a different form when they add their employer's card next.
 * Which card is being edited is the only difference, and it is the difference
 * between the two titles and whether delete is offered.
 *
 * Rendered through a portal rather than in place. On a narrow screen the filter
 * panel is a sheet that is `hidden` rather than unmounted when dismissed, and a
 * dialog inside that subtree would be hidden with it - including from the
 * screen reader, while still holding the focus trap.
 *
 * The `other` bucket is not on the list. It is several hundred hotels, councils
 * and car dealers rather than one network, so a card "accepted at Other" would
 * claim coverage nobody can check; a brand too small for the registry is simply
 * one this editor cannot name yet.
 */

const SELECTABLE_OPERATORS = OPERATOR_OPTIONS.filter((brand) => brand.slug !== OTHER_OPERATOR_SLUG)

const foldedName = (name: string): string => name.trim().toLocaleLowerCase()

export interface ChargeCardEditorProps {
  /** The card being changed, or `null` when defining a new one. */
  readonly card: ChargeCard | null
  /** Every card in the wallet, so a duplicate name can be refused. */
  readonly cards: readonly ChargeCard[]
  readonly onClose: () => void
}

export const ChargeCardEditor = ({ card, cards, onClose }: ChargeCardEditorProps) => {
  const strings = useStrings()
  const addCard = useCardStore((store) => store.addCard)
  const updateCard = useCardStore((store) => store.updateCard)
  const removeCard = useCardStore((store) => store.removeCard)

  const nameId = useId()
  const errorId = useId()

  const [name, setName] = useState(card?.name ?? '')
  const [selected, setSelected] = useState<readonly OperatorSlug[]>(card?.operators ?? [])
  const [error, setError] = useState<string | null>(null)
  // Delete is two presses rather than a `confirm()`: a native dialog cannot be
  // styled, cannot be translated by `strings`, and is blocked outright in some
  // embedded browsers, which would make the card undeletable there.
  const [confirming, setConfirming] = useState(false)

  const held = useMemo(() => new Set(selected), [selected])

  const toggle = (slug: OperatorSlug): void => {
    setError(null)
    setSelected((current) =>
      current.includes(slug) ? current.filter((entry) => entry !== slug) : [...current, slug],
    )
  }

  const submit = (): void => {
    const trimmed = name.trim()
    if (trimmed === '') {
      setError(strings.cardEditor.nameRequired)
      return
    }
    const clash = cards.some(
      (other) => other.id !== card?.id && foldedName(other.name) === foldedName(trimmed),
    )
    if (clash) {
      setError(strings.cardEditor.nameTaken)
      return
    }
    if (selected.length === 0) {
      setError(strings.cardEditor.operatorsRequired)
      return
    }

    const draft = { name: trimmed, operators: selected }
    if (card === null) addCard(draft)
    else updateCard(card.id, draft)
    onClose()
  }

  const remove = (): void => {
    if (card === null) return
    if (!confirming) {
      setConfirming(true)
      return
    }
    removeCard(card.id, card.builtIn)
    onClose()
  }

  const dialog = (
    <Overlay
      title={card === null ? strings.cardEditor.createTitle : strings.cardEditor.editTitle}
      onClose={onClose}
    >
      {/* `noValidate`, because the only required-ness here is the pair rule
          "a name and at least one brand", which a native message cannot say. */}
      <form
        className="card-editor"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <label className="card-editor__field" htmlFor={nameId}>
          <span className="card-editor__label">{strings.cardEditor.nameLabel}</span>
          <input
            className="card-editor__input"
            id={nameId}
            type="text"
            value={name}
            placeholder={strings.cardEditor.namePlaceholder}
            autoComplete="off"
            aria-describedby={error === null ? undefined : errorId}
            onChange={(event) => {
              setError(null)
              setName(event.target.value)
            }}
          />
        </label>

        <fieldset className="card-editor__operators">
          <legend className="card-editor__label">{strings.cardEditor.operatorsLabel}</legend>

          <div className="card-editor__bulk">
            <span className="card-editor__count">
              {strings.cardEditor.operatorsSelected(selected.length)}
            </span>
            <button
              className="ev-button ev-button--quiet"
              type="button"
              onClick={() => {
                setError(null)
                setSelected(SELECTABLE_OPERATORS.map((brand) => brand.slug))
              }}
            >
              {strings.cardEditor.selectAll}
            </button>
            <button
              className="ev-button ev-button--quiet"
              type="button"
              disabled={selected.length === 0}
              onClick={() => setSelected([])}
            >
              {strings.cardEditor.selectNone}
            </button>
          </div>

          <ul className="card-editor__list">
            {SELECTABLE_OPERATORS.map((brand) => (
              <li key={brand.slug}>
                <label className="card-editor__option">
                  <input
                    className="facet__checkbox"
                    type="checkbox"
                    checked={held.has(brand.slug)}
                    onChange={() => toggle(brand.slug)}
                  />
                  <span
                    className="facet__swatch"
                    style={{ backgroundColor: brand.color }}
                    aria-hidden="true"
                  />
                  <span className="card-editor__option-name">{brand.displayName}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        {error === null ? null : (
          <p className="card-editor__error" id={errorId} role="alert">
            {error}
          </p>
        )}

        <p className="card-editor__note">{strings.cardEditor.localNote}</p>

        <div className="card-editor__actions">
          {card === null ? null : (
            <button
              className="ev-button ev-button--danger"
              type="button"
              aria-pressed={confirming}
              onClick={remove}
            >
              {confirming ? strings.cardEditor.removeConfirm : strings.cardEditor.remove}
            </button>
          )}

          <span className="card-editor__spacer" />

          <button className="ev-button ev-button--quiet" type="button" onClick={onClose}>
            {strings.cardEditor.cancel}
          </button>
          <button className="ev-button ev-button--primary" type="submit">
            {strings.cardEditor.save}
          </button>
        </div>
      </form>
    </Overlay>
  )

  // `document.body` is reached at render rather than captured in a ref: the
  // editor only ever exists while the tree is mounted in a browser.
  return createPortal(dialog, document.body)
}
